/** @shell standard @header bar */
import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../shared/theme/sharedStyles';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from '../shared/i18n';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useRequireOnlineOrdersEnabled } from '../shared/navigation/useFeatureGateExit';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  studioCardShell,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';

function createOrderFailedStyles(c: ThemeColors) {
  return StyleSheet.create({
    content: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingVertical: appTheme.spacing.xl,
    },
    iconContainer: {
      marginBottom: appTheme.spacing.lg,
    },
    title: {
      ...appTheme.typography.h1,
      color: c.text,
      marginBottom: appTheme.spacing.sm,
    },
    subtitle: {
      ...appTheme.typography.body,
      color: c.textSecondary,
      textAlign: 'center',
      marginBottom: appTheme.spacing.xl,
    },
    alertCard: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: `${c.warning}15`,
      padding: appTheme.spacing.md,
      gap: appTheme.spacing.md,
      marginBottom: appTheme.spacing.lg,
      alignSelf: 'stretch',
    },
    alertText: {
      flex: 1,
    },
    alertTitle: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '600',
    },
    alertSubtitle: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginTop: 4,
      lineHeight: 20,
    },
    tipsCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.md,
      alignSelf: 'stretch',
    },
    tipsTitle: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '600',
      marginBottom: appTheme.spacing.sm,
    },
    tipRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.xs,
    },
    tipText: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    footer: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
      alignSelf: 'stretch',
      width: '100%',
    },
    footerBtnClickable: { alignSelf: 'stretch' },
    footerBtnInner: { alignSelf: 'stretch' },
  });
}

export const OrderFailedScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const go_back = useExitOrGoBack();
  const online_orders_enabled = useRequireOnlineOrdersEnabled();
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createOrderFailedStyles(themeColors), [themeColors]);
  const { scrollY } = useScreenHeaderScroll();
  const primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(themeColors),
      label: createStackedFooterPrimaryLabelStyle(themeColors),
    }),
    [themeColors],
  );
  const secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(themeColors),
      label: createStackedFooterSecondaryLabelStyle(themeColors),
    }),
    [themeColors],
  );
  const params = (route.params as any) || {};
  const { errorMessage } = params;

  if (!online_orders_enabled) {
    return null;
  }

  return (
    <StackScreenShell colors={themeColors}>
      <ScreenHeader title={t('order.failedTitle')} onBack={go_back} scrollY={scrollY} />
      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <SvgIcon name="x" size={100} color={themeColors.error} />
        </View>

        <Text style={styles.title}>{t('order.failedTitle')}</Text>
        <Text style={styles.subtitle}>{errorMessage || t('order.failedMessage')}</Text>

        <View style={styles.alertCard}>
          <SvgIcon name="alert" size={24} color={themeColors.warning} />
          <View style={styles.alertText}>
            <Text style={styles.alertTitle}>{t('order.whatHappened')}</Text>
            <Text style={styles.alertSubtitle}>{t('order.whatHappenedSubtitle')}</Text>
          </View>
        </View>

        <View style={styles.tipsCard}>
          <Text style={styles.tipsTitle}>{t('order.tryFollowing')}</Text>
          <View style={styles.tipRow}>
            <SvgIcon name="check" size={16} color={themeColors.success} />
            <Text style={styles.tipText}>{t('order.checkCardDetails')}</Text>
          </View>
          <View style={styles.tipRow}>
            <SvgIcon name="check" size={16} color={themeColors.success} />
            <Text style={styles.tipText}>{t('order.ensureBalance')}</Text>
          </View>
          <View style={styles.tipRow}>
            <SvgIcon name="check" size={16} color={themeColors.success} />
            <Text style={styles.tipText}>{t('order.tryDifferentPayment')}</Text>
          </View>
        </View>
      </View>

      <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, appTheme.spacing.md) }]}>
        <Clickable
          onPress={go_back}
          style={styles.footerBtnClickable}
          contentStyle={styles.footerBtnInner}
          accessibilityLabel={t('order.tryAgainButton')}
          accessibilityRole="button">
          <View style={primary_cta.surface}>
            <SvgIcon name="refresh" size={20} color={themeColors.onPrimary} />
            <Text style={primary_cta.label}>{t('order.tryAgainButton')}</Text>
          </View>
        </Clickable>

        <Clickable
          onPress={() => navigation.navigate('MainTabs')}
          style={styles.footerBtnClickable}
          contentStyle={styles.footerBtnInner}
          accessibilityLabel={t('order.backToHome')}
          accessibilityRole="button">
          <View style={secondary_cta.surface}>
            <Text style={secondary_cta.label}>{t('order.backToHome')}</Text>
          </View>
        </Clickable>
      </View>
    </StackScreenShell>
  );
};
